import React, { useState, useRef, useEffect } from 'react';

const API_URL = 'http://localhost:8000/chat';

const SpeechChat: React.FC = () => {
  const [messages, setMessages] = useState<{ sender: 'user' | 'ai'; text: string }[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [loading, setLoading] = useState(false);
  // Use 'any' for recognitionRef since SpeechRecognition is not typed in TS
  const recognitionRef = useRef<any>(null);

  // Text-to-speech for AI responses
  useEffect(() => {
    if (messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.sender === 'ai' && 'speechSynthesis' in window) {
      const utter = new window.SpeechSynthesisUtterance(lastMsg.text);
      utter.lang = 'en-US';
      window.speechSynthesis.speak(utter);
    }
  }, [messages]);

  // Check for browser support
  const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

  const startRecording = () => {
    if (!SpeechRecognition) {
      alert('Speech Recognition not supported in this browser.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    // Use 'any' for event type due to lack of TS types
    recognition.onresult = (event: any) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const transcriptPiece = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          setTranscript(transcriptPiece);
        } else {
          interim += transcriptPiece;
        }
      }
      if (interim) setTranscript(interim);
    };
    recognition.onend = () => {
      setIsRecording(false);
    };
    recognition.onerror = (e: any) => {
      setIsRecording(false);
      alert('Speech recognition error: ' + e.error);
    };
    recognitionRef.current = recognition;
    setTranscript('');
    setIsRecording(true);
    recognition.start();
  };

  const stopRecording = () => {
    recognitionRef.current?.stop();
    setIsRecording(false);
  };

  const handleSend = async () => {
    if (transcript.trim()) {
      setMessages([...messages, { sender: 'user', text: transcript }]);
      setLoading(true);
      const userMessage = transcript;
      setTranscript('');
      try {
        const res = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: userMessage }),
        });
        if (!res.ok) throw new Error('Failed to get AI response');
        const data = await res.json();
        setMessages(msgs => [...msgs, { sender: 'ai', text: data.response }]);
      } catch (err: any) {
        setMessages(msgs => [...msgs, { sender: 'ai', text: 'Error: ' + (err.message || 'Unknown error') }]);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div style={{ maxWidth: 500, margin: '2rem auto', padding: 20, border: '1px solid #ccc', borderRadius: 8 }}>
      <h2>Speech-to-Speech AI Chat</h2>
      <div style={{ minHeight: 200, marginBottom: 16, background: '#f9f9f9', borderRadius: 6, padding: 10 }}>
        {messages.map((msg, idx) => (
          <div key={idx} style={{ textAlign: msg.sender === 'user' ? 'right' : 'left', margin: '8px 0' }}>
            <span style={{
              display: 'inline-block',
              background: msg.sender === 'user' ? '#d1e7dd' : '#e2e3e5',
              padding: '8px 12px',
              borderRadius: 16,
              maxWidth: '80%',
              wordBreak: 'break-word',
            }}>{msg.text}</span>
          </div>
        ))}
        {isRecording && (
          <div style={{ color: '#888', fontStyle: 'italic' }}>Listening... {transcript}</div>
        )}
        {loading && (
          <div style={{ color: '#888', fontStyle: 'italic' }}>AI is thinking...</div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          onClick={isRecording ? stopRecording : startRecording}
          style={{
            background: isRecording ? '#dc3545' : '#198754',
            color: '#fff',
            border: 'none',
            borderRadius: '50%',
            width: 48,
            height: 48,
            fontSize: 24,
            cursor: 'pointer',
          }}
          aria-label={isRecording ? 'Stop recording' : 'Start recording'}
        >
          {isRecording ? '■' : '🎤'}
        </button>
        <input
          type="text"
          value={transcript}
          onChange={e => setTranscript(e.target.value)}
          placeholder="Say something or type..."
          style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid #ccc' }}
          disabled={isRecording}
        />
        <button onClick={handleSend} disabled={!transcript.trim() || loading} style={{ padding: '8px 16px', borderRadius: 6 }}>
          Send
        </button>
      </div>
    </div>
  );
};

export default SpeechChat;
